// View all tables and sync with POS

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { db } from '../../../config/firebase/config';
import { collection, getDocs, getDoc, onSnapshot, serverTimestamp, Timestamp, addDoc, doc, updateDoc } from 'firebase/firestore';
import { retryMesaSync } from '../../../services/firebase/functions';
import { signOut } from 'firebase/auth';
import { auth } from '../../../config/firebase/auth';
import { useRestaurant } from '../../../providers/RestaurantContext';
import MesaCard from './components/MesaCard';  
import { RefreshCw, LogOut, Clock, X, Home } from 'lucide-react';
import { NotificationBell } from '../components/NotificationBell';
import { useNotifications } from '../../../hooks/useNotifications';
import { toast } from 'react-toastify';
import { Mesa } from '../../../types/mesa';
import { createPOSService, POSService } from '../../../services/pos/posService';
import Logo from '../../../assets/fonts/Solo-logo.png';

console.log('🚨 REAL AdminMesasScreen LOADED 🚨');

const FILTER_KEY = 'adminMesasFilter';

const AdminMesasScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { restaurant, isLoading, error: restaurantError, error } = useRestaurant();
  const { list: notificationList } = useNotifications(restaurant?.id);
  // Unread count keyed by mesaId so each card can show its own badge.
  const mesaUnreadCounts = useMemo(() => {
    const counts = new Map<string, number>();
    notificationList.filter((n) => !n.read).forEach((n) => {
      counts.set(n.mesaId, (counts.get(n.mesaId) ?? 0) + 1);
    });
    return counts;
  }, [notificationList]);
  const [mesas, setMesas] = useState<Mesa[]>([]);
  const [filterEstado, setFilterEstado] = useState<string>(() => localStorage.getItem(FILTER_KEY) || 'todas');
  const [showQRCodes, setShowQRCodes] = useState<boolean>(false);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const [logoutMessage, setLogoutMessage] = useState<string>('');
  const [syncingMesaIds, setSyncingMesaIds] = useState<Set<string>>(new Set());
  const [lastGlobalSync, setLastGlobalSync] = useState<Date | null>(null);
  const [posService, setPosService] = useState<POSService | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newMesaNumber, setNewMesaNumber] = useState('');
  const [isCreatingMesa, setIsCreatingMesa] = useState(false);
  const isBulkSyncingRef = useRef(false);
  const lastSyncAllRef = useRef(0);
  const SYNC_ALL_THROTTLE_MS = 25 * 1000;

  // Update clock every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    return () => clearInterval(timer);
  }, []);

  // Handle filter change
  const handleFilterChange = (value: string) => {
    setFilterEstado(value);
    localStorage.setItem(FILTER_KEY, value);
  };

  // Handle QR codes navigation
  const handleQRCodesToggle = () => {
    if (showQRCodes) {
      setShowQRCodes(false);
      navigate('/admin/mesas');
    } else {
      setShowQRCodes(true);
      navigate('/admin/qrcodes');
    }
  };

  // Set QR codes state based on current route
  useEffect(() => {
    if (location.pathname === '/admin/qrcodes') {
      setShowQRCodes(true);
    } else {
      setShowQRCodes(false);
    }
  }, [location.pathname]);

  // Initialize POS service when restaurant data is available
  useEffect(() => {
    if (!restaurant?.id || !restaurant.posConfig) {
      console.warn('⚠️ No POS configuration found for restaurant');
      setPosService(null);
      return;
    }

    try {
      const service = createPOSService(restaurant.id, restaurant.posConfig);
      setPosService(service);
      console.log('✅ POS Service initialized:', restaurant.posConfig.provider);
    } catch (error) {
      console.error('❌ Failed to initialize POS service:', error);
      setPosService(null);
    }
  }, [restaurant?.id, restaurant?.posConfig]);

  const handleSyncFromPOS = async (mesaId: string) => {
    if (!posService) {
      toast.error('POS no configurado para este restaurante');
      return;
    }

    setSyncingMesaIds(prev => new Set(prev).add(mesaId));
    
    try {
      const result = await posService.syncMesa(mesaId);
      
      if (result.success) {
        toast.success(`Mesa actualizada con POS`);
      } else {
        toast.error(`Error: ${result.error || 'No se pudo actualizar'}`);
      }
    } catch (error) {
      console.error('Error syncing with POS:', error);
      toast.error('Error al actualizar con POS');
    } finally {
      setSyncingMesaIds(prev => {
        const next = new Set(prev);
        next.delete(mesaId);
        return next;
      });
    }
  };

  // Retry POS sync for mesas in manual mode
  const handleRetrySync = async (mesaId: string) => {
    setSyncingMesaIds(prev => new Set(prev).add(mesaId));

    try {
      const result = await retryMesaSync({ mesaId });
      const data = result.data as any;

      if (data.success) {
        if (data.mode === 'manual') {
          toast.warning('POS no disponible - permanece en modo manual');
        } else {
          toast.success('Sincronización restaurada correctamente');
        }
        await loadMesas();
      } else {
        toast.error('No se pudo restaurar sincronización');
      }
    } catch (error: any) {
      console.error('Error retrying sync:', error);
      toast.error(error.message || 'Error al reintentar sincronización');
    } finally {
      setSyncingMesaIds(prev => {
        const next = new Set(prev);
        next.delete(mesaId);
        return next;
      });
    }
  };

  // Reintentar = same as opening Detalles on every mesa: sync ALL visible mesas from POS (syncMesaFromPOS), then refresh list
  const handleBulkRetrySync = async () => {
    if (isBulkSyncingRef.current) return;
    if (!posService) {
      toast.error('POS no configurado para este restaurante');
      return;
    }

    const isManualMesa = (mesa: Mesa) =>
      mesa.integrationMode === 'manual' || restaurant?.integrationMode === 'none';
    const isLibreMesa = (mesa: Mesa) =>
      isManualMesa(mesa) ? mesa.status !== 'ocupada' : !mesa.activeSessionId;

    const visibleMesas = mesas.filter((mesa) => {
      if (filterEstado === 'todas') return true;
      const libre = isLibreMesa(mesa);
      if (filterEstado === 'abierta') return !libre;
      if (filterEstado === 'cerrada') return libre;
      return true;
    });
    if (visibleMesas.length === 0) {
      toast.info('No hay mesas para sincronizar');
      return;
    }

    isBulkSyncingRef.current = true;
    setSyncingMesaIds(new Set(visibleMesas.map(m => m.id)));
    toast.info('Sincronizando todas las mesas desde POS...');

    try {
      const results = await Promise.allSettled(
        visibleMesas.map(mesa => posService.syncMesa(mesa.id))
      );
      const fulfilled = results.filter(r => r.status === 'fulfilled') as PromiseFulfilledResult<{ success?: boolean }>[];
      const syncedCount = fulfilled.filter(r => r.value?.success === true).length;
      const rejectedCount = results.filter(r => r.status === 'rejected').length;

      await loadMesas();

      if (syncedCount > 0) {
        toast.success(
          rejectedCount === 0
            ? `${syncedCount} mesa(s) actualizada(s) desde POS`
            : `${syncedCount} mesa(s) actualizada(s). Algunas sin cambios o error.`
        );
      } else if (rejectedCount > 0) {
        toast.warning('Algunas mesas no pudieron actualizarse. Revisa conexión o POS.');
      } else {
        toast.success('Sincronización completada.');
      }
    } catch (error: any) {
      console.error('Error syncing mesas from POS:', error);
      toast.error('Error al sincronizar con POS');
    } finally {
      isBulkSyncingRef.current = false;
      setSyncingMesaIds(new Set());
    }
  };

  // Global sync all tables
  const handleSyncAllFromPOS = async () => {
    if (!posService) {
      toast.error('POS no configurado para este restaurante');
      return;
    }

    const openMesas = mesas.filter(m => m.estado === 'abierta');

    if (openMesas.length === 0) {
      toast.info('No hay mesas abiertas para actualizar');
      return;
    }

    toast.info('Sincronizando mesas desde POS...');
    setSyncingMesaIds(new Set(openMesas.map(m => m.id)));

    try {
      const results = await posService.syncAllOpenMesas(openMesas.map(m => m.id));
      const updatedCount = results.filter(r => r.success).length;
      const noChangeCount = results.length - updatedCount;

      await loadMesas();
      setLastGlobalSync(new Date());

      if (updatedCount > 0) {
        toast.success(
          noChangeCount === 0
            ? `${updatedCount} mesa(s) actualizadas desde POS`
            : `${updatedCount} mesa(s) actualizadas. ${noChangeCount} sin cambios.`
        );
      } else if (noChangeCount > 0) {
        toast.info('Ninguna mesa con cambios. Ya están actualizadas.');
      }
    } catch (error) {
      console.error('Error syncing all mesas:', error);
      toast.error('Error al actualizar mesas');
    } finally {
      setSyncingMesaIds(new Set());
    }
  };

  const loadMesas = useCallback(async () => {
    const restaurantId = restaurant?.id;
    if (!restaurantId || typeof restaurantId !== 'string' || !restaurantId.trim()) return;
    const mesasRef = collection(db, 'restaurants', restaurantId, 'mesas');
    try {
      const snapshot = await getDocs(mesasRef);
      const taxRate = restaurant?.settings?.taxRate ?? 18;
      const posImpuestosIncluded = restaurant?.posConfig?.impuestosIncluded === true;
      const mesasDataRaw = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          ultimaActualizacion: (() => {
            const raw = data.ultimaActualizacion;
            if (!raw) return null;
            if (raw instanceof Timestamp) return raw;
            if (raw.seconds && raw.nanoseconds) return new Timestamp(raw.seconds, raw.nanoseconds);
            return null;
          })(),
          lastPosSync: (() => {
            const raw = data.lastPosSync;
            if (!raw) return null;
            if (raw instanceof Timestamp) return raw;
            if (raw.seconds && raw.nanoseconds) return new Timestamp(raw.seconds, raw.nanoseconds);
            return null;
          })(),
        } as Mesa;
      });
      // Session-driven: bill data (items, totals) live in the session doc, not on the mesa.
      // When mesa.activeSessionId exists, load session and use it for display; mesa doc = metadata only.
      const mesasData = await Promise.all(
        mesasDataRaw.map(async (mesa) => {
          const activeSessionId = (mesa as any).activeSessionId;
          if (!activeSessionId) {
            return { ...mesa, items: Array.isArray((mesa as any).items) ? (mesa as any).items : [], montoTotal: Number((mesa as any).montoTotal) || 0, montoPagado: Number((mesa as any).montoPagado) || 0, restante: Number((mesa as any).restante) || 0 } as Mesa;
          }
          const sessionRef = doc(db, 'restaurants', restaurantId, 'mesas', mesa.id, 'sessions', activeSessionId);
          let session: Record<string, any> | null = null;
          try {
            const sessionSnap = await getDoc(sessionRef);
            if (sessionSnap.exists()) session = sessionSnap.data() as Record<string, any>;
          } catch (e) {
            console.warn('AdminMesasScreen: failed to load session for mesa', mesa.id, activeSessionId, e);
          }
          if (!session) {
            return { ...mesa, items: [], montoTotal: 0, montoPagado: 0, restante: 0, estado: (mesa as any).estado ?? 'abierta' } as Mesa;
          }
          const items = Array.isArray(session.items) ? session.items : [];
          const montoTotal = Number(session.montoTotal) || 0;
          const montoPagado = Number(session.montoPagado) || 0;
          const restante = Number(session.restante) || 0;
          const montoTotalConImpuestos =
            session.montoTotalConImpuestos != null && Number(session.montoTotalConImpuestos) > 0
              ? Number(session.montoTotalConImpuestos)
              : undefined;
          let estado = (session.estado === 'cerrada' || session.estado === 'abierta' ? session.estado : (mesa as any).estado ?? 'abierta') as 'abierta' | 'cerrada';
          // Never show cerrada when there is remaining balance (split/partial payments)
          if (restante > 0.01) estado = 'abierta';
          return {
            ...mesa,
            items,
            montoTotal,
            montoTotalConImpuestos,
            montoPagado,
            restante,
            estado,
            reattachedFromPOS: session.reattachedFromPOS === true,
          } as Mesa;
        })
      );
      setMesas(mesasData.filter((mesa: any) => mesa.isActive !== false));
      // Backfill montoTotalConImpuestos for legacy mesas only (no session)
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.activeSessionId) return;
        const montoTotal = data.montoTotal;
        if (montoTotal != null && (data.montoTotalConImpuestos == null || data.montoTotalConImpuestos === undefined)) {
          const montoTotalConImpuestos = posImpuestosIncluded ? montoTotal : montoTotal * (1 + taxRate / 100);
          const mesaRef = doc(db, 'restaurants', restaurantId, 'mesas', docSnap.id);
          updateDoc(mesaRef, { montoTotalConImpuestos }).catch((err) =>
            console.warn('AdminMesasScreen: backfill montoTotalConImpuestos failed for', docSnap.id, err)
          );
        }
      });
    } catch (err) {
      console.error('AdminMesasScreen: failed to load mesas', err);
    }
  }, [restaurant?.id, restaurant?.settings?.taxRate, restaurant?.posConfig?.impuestosIncluded]);

  // Real-time: payments update mesa root (mirrored totals) + metadata; refresh when any mesa doc changes
  useEffect(() => {
    if (!restaurant?.id) return;
    const mesasRef = collection(db, 'restaurants', restaurant.id, 'mesas');
    let debounce: ReturnType<typeof setTimeout> | undefined;
    let first = true;
    const unsub = onSnapshot(
      mesasRef,
      () => {
        if (first) {
          first = false;
          loadMesas();
          return;
        }
        if (debounce) clearTimeout(debounce);
        debounce = setTimeout(() => loadMesas(), 350);
      },
      (err) => console.error('AdminMesasScreen: mesas onSnapshot', err)
    );
    return () => {
      if (debounce) clearTimeout(debounce);
      unsub();
    };
  }, [restaurant?.id, loadMesas]);

  // Background sync of all mesas when list is shown (throttled) so OPEN tickets show without opening Detalles
  useEffect(() => {
    if (!posService || mesas.length === 0) return;
    const hasPOS = restaurant?.posConfig?.enabled && restaurant?.integrationMode && restaurant.integrationMode !== 'none';
    if (!hasPOS) return;
    if (Date.now() - lastSyncAllRef.current < SYNC_ALL_THROTTLE_MS) return;

    lastSyncAllRef.current = Date.now();
    const syncAll = async () => {
      await Promise.allSettled(mesas.map((m) => posService!.syncMesa(m.id)));
      loadMesas();
    };
    const t = setTimeout(syncAll, 400);
    return () => clearTimeout(t);
  }, [posService, mesas.length, restaurant?.posConfig?.enabled, restaurant?.integrationMode, loadMesas]);

  // When user returns to tab, allow sync again after throttle (so coming back from Detalles refreshes list)
  useEffect(() => {
    const onFocus = () => {
      if (!posService || mesas.length === 0) return;
      const hasPOS = restaurant?.posConfig?.enabled && restaurant?.integrationMode && restaurant.integrationMode !== 'none';
      if (!hasPOS) return;
      if (Date.now() - lastSyncAllRef.current < SYNC_ALL_THROTTLE_MS) return;
      lastSyncAllRef.current = Date.now();
      Promise.allSettled(mesas.map((m) => posService!.syncMesa(m.id))).then(() => loadMesas());
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [posService, mesas.length, restaurant?.posConfig?.enabled, restaurant?.integrationMode, loadMesas]);

  // On mount, sync filter from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(FILTER_KEY);
    if (stored && stored !== filterEstado) {
      setFilterEstado(stored);
    }
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const handleCreateMesa = async () => {
    if (!restaurant?.id) {
      toast.error('No se pudo obtener información del restaurante');
      return;
    }

    if (!newMesaNumber.trim()) {
      toast.error('Por favor ingresa un número de mesa');
      return;
    }

    // Check if mesa number already exists
    const existingMesa = mesas.find(m => m.numeroMesa === newMesaNumber.trim());
    if (existingMesa) {
      toast.error(`La mesa ${newMesaNumber} ya existe`);
      return;
    }

    setIsCreatingMesa(true);

    try {
      // ========== CRITICAL: Check BOTH conditions for POS integration ==========
      // A restaurant has POS integration ONLY if BOTH are true:
      // 1. integrationMode !== "none" (HOW to sync)
      // 2. posProvider !== "none" (WHO to sync with)
      const restaurantIntegrationMode = restaurant.integrationMode || 'none';
      const posProvider = restaurant.posConfig?.provider || 'none';
      
      // Check if restaurant has REAL POS integration
      const hasPOSIntegration = 
        restaurantIntegrationMode !== 'none' && 
        posProvider !== 'none';
      
      let mesaIntegrationMode: 'api' | 'webhook' | 'polling' | 'manual';
      let mesaActiveSource: 'api' | 'webhook' | 'polling' | 'manual';
      
      if (!hasPOSIntegration) {
        // NO POS integration → mesa is manual-only
        mesaIntegrationMode = 'manual';
        mesaActiveSource = 'manual';

        console.log('⚠️ Mesa created in manual mode: No POS integration available', {
          integrationMode: restaurantIntegrationMode,
          posProvider
        });
      } else {
        // Restaurant has REAL POS → mesa inherits that mode
        mesaIntegrationMode = restaurantIntegrationMode;
        mesaActiveSource = restaurantIntegrationMode;
        console.log('✅ Mesa created with POS integration:', {
          integrationMode: mesaIntegrationMode,
          posProvider
        });
      }

      // Create new mesa document
      const newMesaData = {
        numeroMesa: newMesaNumber.trim(),
        restaurantId: restaurant.id,
        isActive: true,
        status: 'libre' as const,
        estado: 'cerrada' as const,
        activeSessionId: null,
        integrationMode: mesaIntegrationMode,
        activeSource: mesaActiveSource,
        syncStatus: 'ok' as const,
        createdAt: serverTimestamp(),
        ultimaActualizacion: serverTimestamp(),
      };

      if (!restaurant.id?.trim()) {
        toast.error('Restaurant ID faltante. No se puede crear la mesa.');
        return;
      }
      await addDoc(collection(db, 'restaurants', restaurant.id, 'mesas'), newMesaData);

      toast.success(`Mesa ${newMesaNumber} creada exitosamente`);
      
      // Reset form and close modal
      setNewMesaNumber('');
      setShowCreateModal(false);
    } catch (error: any) {
      console.error('Error creating mesa:', error);
      toast.error(`Error al crear mesa: ${error.message}`);
    } finally {
      setIsCreatingMesa(false);
    }
  };

  const isManualMesaForFilter = (mesa: Mesa) =>
    mesa.integrationMode === 'manual' || restaurant?.integrationMode === 'none';
  const isLibreMesaForFilter = (mesa: Mesa) =>
    isManualMesaForFilter(mesa) ? mesa.status !== 'ocupada' : !mesa.activeSessionId;

  const filteredMesas = [...mesas]
    .filter((mesa) => {
      if (filterEstado === 'todas') return true;
      const libre = isLibreMesaForFilter(mesa);
      if (filterEstado === 'abierta') return !libre;
      if (filterEstado === 'cerrada') return libre;
      return true;
    })
    .sort((a, b) => {
      const aNum = a.numeroMesa ? parseInt(String(a.numeroMesa), 10) : 0;
      const bNum = b.numeroMesa ? parseInt(String(b.numeroMesa), 10) : 0;
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      // Fallback to string comparison if parsing fails
      const aStr = String(a.numeroMesa || '');
      const bStr = String(b.numeroMesa || '');
      return aStr.localeCompare(bStr, 'es', { numeric: true, sensitivity: 'base' });
    });

  const getEmptyStateMessage = () => {
    switch (filterEstado) {
      case 'abierta': return "No hay mesas ocupadas para mostrar.";
      case 'cerrada': return "No hay mesas libres para mostrar.";
      default: return "No hay mesas registradas.";
    }
  };

  // Error display and logging
  if (error) {
    console.error('Restaurant context error:', error);
    return <div className="p-4 text-red-600">Error: {error}</div>;
  }

  // Render guard: don't render unless restaurant is loaded
  if (isLoading || !restaurant?.id) {
    console.log('⏳ Waiting for restaurant context... isLoading:', isLoading, 'restaurant:', restaurant);
    return <div className="p-4">Cargando restaurante...</div>;
  }

  if (restaurantError) {
    return <div className="error-container"><p className="error-text">{restaurantError}</p></div>;
  }

  return (
    <div className="min-h-screen w-full flex flex-col px-6 py-6 animate-fade-in">
      {/* Header Bar */}
      <div className="flex items-center justify-between mb-8 w-full relative">
        {/* Left Side - Logo + YAP Text */}
        <div className="flex items-center">
          <Link to="/admin" className="flex items-center gap-3 group">
            <img 
              src={Logo} 
              alt="YAP Logo" 
              className="h-10 w-auto object-contain"
            />
            <span className="text-3xl font-bold tracking-tighter text-white group-hover:text-yap-primary transition-colors">
              yap
            </span>
          </Link>
        </div>

        {/* Center - Clock Display */}
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-zinc-900/40 backdrop-blur-xl border border-white/5 rounded-full">
          <Clock size={16} className="text-zinc-400" />
          <div className="flex flex-col">
            <span className="text-sm font-bold text-white leading-none">
              {currentTime.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })}
            </span>
            <span className="text-[10px] text-zinc-500 font-medium leading-none mt-0.5">
              {currentTime.toLocaleDateString('es-DO', { weekday: 'short', day: 'numeric', month: 'short' })}
            </span>
          </div>
        </div>

        {/* Right Side - Volver a inicio + Bell + Logout */}
        <div className="flex items-center gap-4">
          <Link
            to="/admin"
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors font-medium"
          >
            <Home size={18} />
            <span>Volver a inicio</span>
          </Link>
          <div className="w-px h-6 bg-zinc-700" />
          <NotificationBell />
          <div className="w-px h-6 bg-zinc-700"></div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-red-500 transition-colors font-medium"
          >
            <LogOut size={18} />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto w-full">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {/* Left side - Title, Subtitle, and Sync Button */}
            <div>
              <h1 className="text-4xl md:text-32px font-bold text-white tracking-tight mb-1">
                Gestión de Mesas
              </h1>
              <div className="flex items-center gap-3">
                <p className="text-zinc-400 text-base">Vista en tiempo real</p>
                
                {/* Small Bulk Retry Sync button */}
                {(() => {
                  const hasPOSConfigured = restaurant && 
                    restaurant.integrationMode !== 'none' && 
                    restaurant.posConfig?.provider !== 'none';
                  
                  const hasUnsyncedMesas = hasPOSConfigured && mesas.some(mesa => {
                    const isActuallySynced = 
                      mesa.syncStatus === 'ok' &&
                      mesa.lastPosSync !== null &&
                      mesa.activeSource !== 'manual';
                    return !isActuallySynced && mesa.integrationMode !== 'manual';
                  });

                  return hasPOSConfigured && hasUnsyncedMesas ? (
                    <button
                      onClick={handleBulkRetrySync}
                      disabled={syncingMesaIds.size > 0}
                      title="Reintentar sincronización de todas las mesas"
                      className="flex items-center gap-1 px-2 py-0.5 bg-orange-900/20 text-orange-400 border border-orange-500/20 rounded text-[10px] font-medium hover:bg-orange-800/30 hover:text-orange-300 transition-all disabled:opacity-50"
                    >
                      <RefreshCw className={`w-2.5 h-2.5 ${syncingMesaIds.size > 0 ? 'animate-spin' : ''}`} />
                      Reintentar
                    </button>
                  ) : null;
                })()}
              </div>
            </div>

            {/* Right side - Filters and Nueva Mesa button */}
            <div className="flex flex-wrap gap-3">
              {/* Status filters */}
              <div className="flex items-center bg-zinc-900 rounded-full p-1 border border-white/10">
                {[
                  { label: 'Todas', value: 'todas' },
                  { label: 'Abiertas', value: 'abierta' },
                  { label: 'Cerradas', value: 'cerrada' },
                ].map(tab => (
                  <button
                    key={tab.value}
                    className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
                      filterEstado === tab.value 
                        ? 'bg-zinc-800 text-white shadow-sm' 
                        : 'text-zinc-500 hover:text-zinc-300'
                    }`}
                    onClick={() => handleFilterChange(tab.value)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              
              {/* Nueva Mesa button */}
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-5 py-2 bg-yap-primary text-black rounded-full text-sm font-bold hover:bg-yap-hover transition-all shadow-[0_0_20px_-5px_rgba(0,255,132,0.3)] hover:scale-105 active:scale-95"
              >
                <span className="text-lg">+</span>
                Nueva Mesa
              </button>
            </div>
          </div>
        </div>

        {/* Only show mesas content when not showing QR codes */}
        {!showQRCodes && (
          <section className="pb-12">
            {filteredMesas.length === 0 ? (
              <div className="text-zinc-500 italic text-center py-12 bg-zinc-900/20 rounded-2xl border border-white/5">{getEmptyStateMessage()}</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {filteredMesas.map(mesa => (
                  <MesaCard
                    key={mesa.id}
                    mesa={mesa}
                    onSyncFromPOS={handleSyncFromPOS}
                    onRetrySync={handleRetrySync}
                    isSyncing={syncingMesaIds.has(mesa.id)}
                    onMesaClosed={loadMesas}
                    onMesaUpdated={loadMesas}
                    unreadNotificationCount={mesaUnreadCounts.get(mesa.id) ?? 0}
                  />
                ))}
              </div>
            )}
          </section>
        )}
      </div>

      {/* Create Mesa Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl border border-white/10 p-6 max-w-md w-full shadow-2xl animate-fade-in">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Nueva Mesa</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewMesaNumber('');
                }}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <X size={20} className="text-zinc-400" />
              </button>
            </div>

            {/* Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Número de Mesa
                </label>
                <input
                  type="text"
                  value={newMesaNumber}
                  onChange={(e) => setNewMesaNumber(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isCreatingMesa) {
                      handleCreateMesa();
                    }
                  }}
                  placeholder="Ej: 1, 2, 3, A1, B2..."
                  className="w-full px-4 py-3 bg-zinc-800/50 border border-white/10 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-yap-primary focus:border-transparent"
                  autoFocus
                />
                <p className="text-xs text-zinc-500 mt-2">
                  Ingresa un identificador único para la mesa
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewMesaNumber('');
                  }}
                  className="flex-1 px-4 py-3 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors font-semibold"
                  disabled={isCreatingMesa}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreateMesa}
                  disabled={
                    isCreatingMesa ||
                    !newMesaNumber.trim()
                  }
                  className="flex-1 px-4 py-3 bg-yap-primary text-black rounded-lg hover:bg-yap-hover transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_20px_-5px_rgba(0,255,132,0.3)]"
                >
                  {isCreatingMesa ? 'Creando...' : 'Crear Mesa'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMesasScreen;